import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowRight, Code, Globe, Smartphone, Database, Shield, Users } from 'lucide-react';

const WebDevelopment = () => {
  const capabilities = [
    {
      icon: <Code className="h-6 w-6" />,
      title: "Custom Web Applications",
      description: "Tailored solutions built with React, Node.js, and modern frameworks"
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Responsive Websites", 
      description: "Mobile-first designs that work perfectly across all devices"
    },
    {
      icon: <Database className="h-6 w-6" />,
      title: "Database Integration",
      description: "Seamless data management with MongoDB, PostgreSQL, and cloud services"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Security & Performance",
      description: "Enterprise-grade security and optimized performance"
    },
    {
      icon: <Smartphone className="h-6 w-6" />,
      title: "Progressive Web Apps",
      description: "App-like experiences that work offline and load instantly"
    }
  ];

  const whyVincotek = [
    "Innovation-first approach with cutting-edge technology stack",
    "Proven track record with startups and growing businesses", 
    "Ongoing support and maintenance after launch",
    "Transparent development process with regular updates"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-vincotek-blue to-blue-600 text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Web Development
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Custom websites and web applications built with modern technologies for optimal performance and exceptional user experience.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8 text-center">
              What We Do
            </h2>
            <p className="text-lg text-gray-600 mb-12 text-center leading-relaxed">
              At Vincotek, we create powerful web solutions that help businesses succeed online. From simple websites to complex web applications, we deliver high-quality, scalable solutions that grow with your business.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-6">
                    <div className="text-vincotek-blue mb-4">
                      {capability.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-vincotek-dark mb-3">
                      {capability.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {capability.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8">
              Who This Is For
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our web development services are perfect for startups looking to establish their online presence, growing SMEs needing scalable solutions, and established businesses requiring digital transformation. Whether you need a simple corporate website, complex e-commerce platform, or custom web application, we work with businesses of all sizes to create digital solutions that drive growth and engagement.
            </p>
          </div>
        </div>
      </section>

      {/* Why Vincotek Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Why Choose Vincotek
            </h2>
            <div className="space-y-6">
              {whyVincotek.map((reason, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-vincotek-yellow rounded-full flex items-center justify-center">
                    <span className="text-vincotek-dark font-bold text-sm">{index + 1}</span>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {reason}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Your Web Solution?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss your project and create a web solution that drives results for your business.
          </p>
          <Link to="/contact">
            <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
              Contact Us Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WebDevelopment;